/* eslint-disable no-extra-boolean-cast */
/* eslint-disable react-native/no-inline-styles */
/**
 * Login Screen
 * 
 * Handles user authentication with email/mobile and password
 * Features:
 * - Validates email or 10-digit mobile number
 * - Password field with show/hide toggle
 * - Formik form validation
 * - Stores access/refresh tokens on success
 * - Fetches user profile, app info, and payment methods after login
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 */
import React, { useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Text, TextInput } from 'react-native-paper';
import { useFormik } from 'formik/dist';
import * as Yup from 'yup';
import { flexContainer, theme } from '../theme/theme';
import { doPost } from '../services/http';
import * as storage from '../services/storage';
import { useDispatch, useSelector } from 'react-redux';
import { Email_RegExp, errorHandler, getCompanyInfo, getKeyboardBehavior, getPaymentGateways, getProfile, Mobile_RegExp } from '../services/Utils';
import BackButton from '../components/BackButton';
import { useIsFocused } from '@react-navigation/native';
import { hideLoader, showLoader } from '../store/info.slice';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AggrLogo from '../components/AggrLogo';
import RecaptchaV2 from '../components/RecaptchaV2';
import RecaptchaNotice from '../components/RecaptchaNotice';
import { getRecaptchaChallenge, getRecaptchaHeaders } from '../services/Recaptcha';

const InputRoundness = 32;

function Login({ navigation }) {
    const dispatch = useDispatch();
    const info = useSelector((state) => state.info?.info);
    const isFocused = useIsFocused();
    const insets = useSafeAreaInsets();

    const mobileInput = useRef(null);
    const passwordInput = useRef(null);

    const isKeyboardVisible = false;
    const [secureTextEntry, setSecureTextEntry] = useState(true);
    const [captchaChallenge, setCaptchaChallenge] = useState(null);
    const [captchaV2Token, setCaptchaV2Token] = useState('');

    const scheme = {
        mobile: Yup.string()
            .required('Email or mobile number is required')
            .test(
                'is-email-or-mobile',
                'Enter a valid email or mobile number',
                value => {
                    return Email_RegExp.test(value) || Mobile_RegExp.test(value);
                }
            ),
        password: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Password is required'),
    };
    const LoginSchema = Yup.object().shape(scheme);
    // ✅ useFormik Hook
    const formik = useFormik({
        initialValues: { mobile: '', password: '' },
        validationSchema: LoginSchema,
        onSubmit: (values) => {
            console.log('Login Data:', values);
            onSubmitHandler(values);
        },
    });

    const onSubmitHandler = async (values) => {
        try {
            if (captchaChallenge && !captchaV2Token) { return; }
            dispatch(showLoader());
            const headers = await getRecaptchaHeaders(info?.recaptcha, 'cash_counter_login', captchaV2Token);
            const response = await doPost('/login', values, { headers });
            console.log(response.data);
            formik.setSubmitting(false);
            storage.storeData('accessToken', response.data.accessToken);
            await getCompanyInfo();
            await getProfile();
            await getPaymentGateways();
            dispatch(hideLoader());
            navigation.navigate('Home');
        } catch (error) {
            console.error(error);
            formik.setSubmitting(false);
            dispatch(hideLoader());
            const challenge = getRecaptchaChallenge(error, info?.recaptcha);
            if (challenge) {
                setCaptchaChallenge(challenge);
                setCaptchaV2Token('');
                return;
            }
            errorHandler(error);
        }
    };


    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setSecureTextEntry((prevState) => !prevState);
    };

    return (
        <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.primary }]}>
            <View style={{ padding: 16 }}>
                <Text variant="titleLargeBold" style={{ color: '#ffffff' }}>Login</Text>
                <Text variant="textLight" style={{ color: '#ffffff' }}>Login to continue using the app</Text>
            </View>
            <KeyboardAvoidingView behavior={getKeyboardBehavior} style={[styles.mainContainer, flexContainer]} keyboardVerticalOffset={16}>
                <ScrollView contentContainerStyle={styles.KeyboardAwareScrollView}>
                    <AggrLogo />
                    <View>
                        {/* ✅ Mobile Input */}
                        <View style={styles.inputContainer}>
                            <Text variant="titleMedium">{'Mobile/Email'}</Text>
                            <TextInput
                                ref={mobileInput}
                                value={formik.values.mobile}
                                onChangeText={formik.handleChange('mobile')}
                                onBlur={formik.handleBlur('mobile')}
                                placeholder={'Enter your mobile number or email'}
                                mode="outlined"
                                keyboardType={'default'}
                                theme={{ roundness: InputRoundness }}
                                style={styles.input}
                                contentStyle={styles.contentStyle}
                                returnKeyType={'next'}
                                onSubmitEditing={() => {
                                    passwordInput.current.focus();
                                }}
                            />
                            {formik.touched.mobile && formik.errors.mobile && (
                                <Text style={styles.errorText}>{formik.errors.mobile}</Text>
                            )}
                        </View>

                        {/* ✅ Password Input */}
                        <View style={styles.inputContainer}>
                            <Text variant="titleMedium">Password</Text>
                            <TextInput
                                ref={passwordInput}
                                value={formik.values.password}
                                onChangeText={formik.handleChange('password')}
                                onBlur={formik.handleBlur('password')}
                                placeholder="Enter Password"
                                mode="outlined"
                                secureTextEntry={secureTextEntry}
                                theme={{ roundness: InputRoundness }}
                                style={styles.input}
                                contentStyle={styles.contentStyle}
                                onSubmitEditing={formik.handleSubmit}
                                right={<TextInput.Icon color={theme.colors.primary} onPress={togglePasswordVisibility} size={24} icon={secureTextEntry ? 'eye-off' : 'eye'} />}
                            />
                            {formik.touched.password && formik.errors.password && (
                                <Text style={styles.errorText}>{formik.errors.password}</Text>
                            )}
                        </View>

                        {captchaChallenge?.siteKey ? (
                            <RecaptchaV2
                                key={`${captchaChallenge.code}-${captchaChallenge.siteKey}-${captchaChallenge.attempt}`}
                                siteKey={captchaChallenge.siteKey}
                                onToken={setCaptchaV2Token}
                            />
                        ) : null}

                        {/* ✅ Submit Button */}
                        {!isKeyboardVisible && (
                            <Button
                                theme={{ roundness: 36 }}
                                mode="contained"
                                labelStyle={styles.labelStyle}
                                onPress={formik.handleSubmit}
                                disabled={formik?.isSubmitting || (captchaChallenge && !captchaV2Token)}
                            >
                                {formik?.isSubmitting ? 'Submitting...' : 'Login'}
                            </Button>
                        )}

                        {/* ✅ Forgot Password */}
                        <View style={styles.inputContainer}>
                            <Text
                                onPress={() => navigation.navigate('ForgotPassword')}
                                variant="titleSmall"
                                style={styles.forgotPasswordText}
                            >
                                Forgot Password?
                            </Text>
                        </View>
                        <RecaptchaNotice visible={info?.recaptcha?.enabled} />
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>
            {isFocused && Platform.OS === 'android' && <BackButton isRoot={true} />}
        </SafeAreaView>
    );
}

// 🔹 Styles
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    mainContainer: {
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 16,
        margin: 8,
    },
    KeyboardAwareScrollView: {
        flexGrow: 1,
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 8,
        paddingBottom: 64,
    },
    input: {
        fontSize: 14,
        backgroundColor: '#f6f8fa',
        marginVertical: 4,
    },
    contentStyle: {
        paddingLeft: 16,
    },
    inputContainer: {
        marginVertical: 8,
    },
    labelStyle: {
        fontSize: 16,
        letterSpacing: 2,
        paddingVertical: 4,
    },
    signUpText: {
        textDecorationLine: 'underline',
        marginLeft: 4,
    },
    forgotPasswordText: {
        marginTop: 16,
        textAlign: 'center',
        textDecorationLine: 'underline',
    },
    errorText: {
        color: 'red',
        fontSize: 12,
        marginTop: 2,
    },
    cameraWrapper: {
        width: '100%',
        height: 256,
        borderRadius: 12,
        overflow: 'hidden',
    },
});

export default Login;
